import { useStore } from '~/composables/useStore';
import { logApiFailure } from '~/utils/logApiFailure';
import { LSLocationSchema, type LSLocation } from '#shared/types/location';

const KEY = 'lcp-locations';

interface UseLocationStoreOptions {
	immediate?: boolean;
}

export function useLocationStore(options?: UseLocationStoreOptions) {
	const { immediate = true } = options || {};
	const { $api } = useNuxtApp();

	const { data, loading, error, refresh } = useStore<LSLocation[]>({
		key: KEY,
		immediate,
		lazy: true,
		server: false,
		fetcher: async () => {
			try {
				const locations = await $api('/api/Locations', {
					schema: LSLocationSchema.array(),
				});
				frogger.debug('locations store loaded', { count: locations.length });
				return locations;
			} catch (error) {
				frogger.warn('locations load failed — using empty list', error, {
					entity: 'location',
				});
				return [];
			}
		},
	});

	async function saveLocation(location: LSLocation) {
		try {
			await $api('/api/Locations', {
				method: 'POST',
				// Coerce before POST (e.g. postCode `z.coerce.string()`) so the body
				// matches the backend contract regardless of form input types.
				body: LSLocationSchema.parse(location),
			});
		} catch (err) {
			logApiFailure('entity save failed', err, {
				entity: 'location',
				id: location.locationKey,
				context: 'saveLocation',
			});
			throw err;
		}
		await refresh();
	}

	return {
		data,
		loading,
		fetching: loading,
		error,
		refresh,
		saveLocation,
	};
}
